import { FormModelChangeHandler } from "@/types/forms/types";
import { PriceCalculatorFormData } from "@/features/price-calculator/types/types";
import { Box, Row, Space } from "@stenajs-webui/core";
import { FieldCaption } from "@/components/forms/FieldCaption";
import { GoodsSelect } from "@/components/data-driven-inputs/selects/goods-select/GoodsSelect";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import {
  FlatButton,
  SecondaryButton,
  stenaMinus,
  stenaPlusCircle,
} from "@stenajs-webui/elements";
import { NumericAgrVehicleWeightTextInput } from "@/components/numeric-agr-vehicle-weight-text-input/NumericAgrVehicleWeightTextInput";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { GapValues } from "@/lib/utils/GapValues";
import { ConditionalTooltip } from "@/components/ConditionalTooltip";
import { replaceAtIndex } from "@/lib/list/ReplaceAtIndex";

interface Props {
  formData: PriceCalculatorFormData;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
}

export const PriceCalculatorAddGoodsRow: React.FC<Props> = ({
  formData,
  onChangeFormData,
}) => {
  const isGoodsWeightEmpty = (index: number) =>
    formData?.conditionsGoods?.[index]?.weight === "";

  return (
    <Row gap={GapValues.TWENTYFOUR}>
      <Box width={200}>
        <FieldCaption label={"Goods code"}>
          {formData?.conditionsGoods?.map((goods, index) => (
            <Box key={`goods-select-${index}`}>
              <GoodsSelect
                value={goods.goodsCode}
                onValueChange={(goodsCode) =>
                  onChangeFormData(
                    "conditionsGoods",
                    replaceAtIndex(formData.conditionsGoods ?? [], index, {
                      goodsCode,
                    })
                  )
                }
                height={"38px"}
                menuPortalTarget={document.body}
                menuPlacement={"auto"}
                minMenuHeight={300}
              />
              <Space num={SpaceValues.SIXTEEN} />
            </Box>
          ))}
          <SecondaryButton
            label={"Add goods"}
            leftIcon={stenaPlusCircle}
            onClick={() => {
              onChangeFormData("conditionsGoods", [
                ...(formData?.conditionsGoods || []),
                { goodsCode: "", weight: "0" },
              ]);
            }}
            size={"medium"}
          />
        </FieldCaption>
      </Box>
      <FieldCaption label={"Weight (kg)"}>
        {formData.conditionsGoods?.length === 0 ? (
          <Row gap={GapValues.TWENTYFOUR}>
            <NumericAgrVehicleWeightTextInput disabled />
            <FlatButton leftIcon={stenaMinus} />
          </Row>
        ) : (
          formData?.conditionsGoods?.map((goods, index) => (
            <Row gap={GapValues.TWENTYFOUR} key={`weight-row-${index}`}>
              <Box width={187}>
                <ConditionalTooltip
                  label={
                    isGoodsWeightEmpty(index) ? "Please provide a weight" : ""
                  }
                  zIndex={ZIndex.lowest}
                  variant="warning"
                  appendTo={document.body}
                  shouldShow={isGoodsWeightEmpty(index)}
                >
                  <NumericAgrVehicleWeightTextInput
                    value={goods.weight}
                    onValueChange={(weight) =>
                      onChangeFormData(
                        "conditionsGoods",
                        replaceAtIndex(formData.conditionsGoods ?? [], index, {
                          weight,
                        })
                      )
                    }
                    variant={isGoodsWeightEmpty(index) ? "warning" : "standard"}
                  />
                </ConditionalTooltip>
                <Space num={SpaceValues.SIXTEEN} />
              </Box>
              <FlatButton
                leftIcon={stenaMinus}
                onClick={() =>
                  onChangeFormData("conditionsGoods", [
                    ...(formData?.conditionsGoods || []).filter(
                      (item, formIndex) => index !== formIndex
                    ),
                  ])
                }
              />
            </Row>
          ))
        )}
      </FieldCaption>
    </Row>
  );
};
